/*
 *   File : listing.js
 *   Author : https://evoqins.com
 *   Description : Subscription listing page
 *   Integrations : 
 *   Version : 1.0.0
*/


import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Modal } from "bootstrap";
import { toast } from "react-toastify";

// Components
import { EmptyScreen, Loader, OverLay } from "../../Component/Others";
import { PaymentBreakDownModal, SubscriptionFilterModal } from "../../Component/Modal";
import { SubscriptionCard } from "../../Component/Cards";
import { APIService } from "../../Service";
import { _scrollToTop } from "../../Helper";

// Assets
import EmptySubscription from '../../Assets/Images/EmptyScreen/empty-subscription.webp';

// Custom data
const ACTIVE_STATUS = [
    // { id: null, label: "All" },
    { id: "CREATED", label: "Created" },
    { id: "AUTHORIZED", label: "Authorized" },
    { id: "ACTIVE", label: "Active" },
    { id: "EXPIRED", label: "Expired" },
    { id: "MANDATE_EXPIRED", label: "Mandate expired" },
    { id: "CANCELLED", label: "Cancelled" },
    { id: "AUTO_RENEWAL", label: "Auto renewal" },
    { id: "AWAITING_RENEWAL", label: "Awaiting renewal" }
];

// const PRODUCT_TYPE = [
//     // { id: null, label: "All" },
//     { id: "BASKET", label: "Basket" },
//     { id: "STOCK", label: "Stock" },
//     { id: "MUTUAL_FUND", label: "Mutual fund" },
//     { id: "OPTION", label: "Option" }
// ];

function SubscriptionListing() {

    const navigate = useNavigate();
    const USER_DATA = useSelector((store) => store.Reducer.USER_DATA);
    const breakDownInfo = useRef({});
    const initialRender = useRef(true);

    const [toggleFilter, setToggleFilter] = useState(false);
    const [selectedStatusIds, setSelectedStatusIds] = useState("ACTIVE");
    const [selectedProductTypeIds, setSelectedProductTypeIds] = useState("BASKET");
    const [activeList, setActiveList] = useState([]);
    const [dataLoading, setDataLoading] = useState(true);
    const [btnLoading, setBtnLoading] = useState(true);
    const [subsID, setSubsID] = useState(-1); // For showing loader only on clicked item for cancel subscription
    const [showPaymentBreakDownModal, setShowPaymentBreakDownModal] = useState(false);
    const [cancelledIDs, setCancelledIDs] = useState([]);

    useEffect(() => {
        // Scroll the page to top
        _scrollToTop();

        // API call
        _getSubscriptionList();

    }, []);

    useEffect(() => {
        if (showPaymentBreakDownModal === true) {
            const subscription_payment_modal = new Modal(document.getElementById('subscription-payment-breakdown-modal'));
            subscription_payment_modal.show();
        }
    }, [showPaymentBreakDownModal]);

    useEffect(() => {

        if (toggleFilter) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }

    }, [toggleFilter])


    // handle toggle filter 
    const _handleToggleFilter = () => {
        setToggleFilter(!toggleFilter);
    };

    // handle cancel filter 
    const _handleCancelFilter = () => {
        setToggleFilter(false);
        setSelectedStatusIds([]);
        setSelectedProductTypeIds([]);
    };

    // handle select risk filter
    const _handleFilterByStatus = (id) => {
        setSelectedStatusIds(id);

        // if (selectedStatusIds.includes(id)) {
        //     setSelectedStatusIds(selectedStatusIds.filter(risk_id => risk_id !== id));
        // } else {
        //     setSelectedStatusIds([...selectedStatusIds, id]);
        // }
    };

    // handle select objective filter 
    const _handleFilterByProduct = (id) => {

        setSelectedProductTypeIds(id);

        // if (selectedProductTypeIds.includes(id)) {
        //     setSelectedProductTypeIds(selectedProductTypeIds.filter(objective_id => objective_id !== id));
        // } else {
        //     setSelectedProductTypeIds([...selectedProductTypeIds, id]);
        // }
    };

    // handle clear filter 
    const _handleClearFilter = () => {
        setSelectedStatusIds([]);
        setSelectedProductTypeIds([]);
    };

    // handle apply filter 
    const _handleApplyFilter = () => {
        setToggleFilter(false);
        setDataLoading(true);
        // API Call
        _getSubscriptionList();
    };


    // handle navigate to explore page
    const _handleStartInvesting = () => {
        navigate("/explore");
    };


    // handle cancel subscription
    const _handleCancelSubscription = (sub_id, product_name) => {
        setSubsID(sub_id);
        setBtnLoading(true)
        // API call
        _cancelActiveSubscription(sub_id, product_name);
    };

    // handle open payment break down modal
    const _handleOpenPaymentBreakDownModal = (product) => {
        setShowPaymentBreakDownModal(true);

        let subscription_fee = 0, gst = 0;

        if (product.plan.fee_type === "PERCENTAGE") {
            subscription_fee = (Number(product.planned_investment_amount) * (Number(product.plan.percentage_fee) / 100)).toFixed(2);
            gst = (subscription_fee * (18 / 100)).toFixed(2);
        } else {
            subscription_fee = product.plan.fixed_fee.toFixed(2);
            gst = (subscription_fee * (18 / 100)).toFixed(2);
        }

        breakDownInfo.current = {
            subscription_fee: Number(subscription_fee),
            gst: Number(gst),
            total: Number(subscription_fee) + Number(gst)
        };
    };

    // handle close payment break down modal
    const _handleClosePaymentBreakdownModal = () => {
        setShowPaymentBreakDownModal(false);
    };


    // =============================================== //
    // ================= API calls =================== //
    // Get States list
    const _getSubscriptionList = async (success_msg, cancelled_list = []) => {

        let url = "subscription/get";
        let data = {
            "user_ids": [USER_DATA.unique_identifier],
            "status": selectedStatusIds,
            // "plan_ids": [],
            // "tenant_ids": [],
            "type": "BASKET",
        };

        const response = await APIService(url, data);
        if (response.status_code === 200) {
            let updated_list = response.data.filter(item => !cancelled_list.includes(item.id))
            setActiveList(updated_list);
            if (success_msg) {
                toast.dismiss();
                toast.success(success_msg);
            }
            initialRender.current = false;
        } else {
            setActiveList([]);
        }
        setBtnLoading(false);
        setDataLoading(false);
    };

    // Cancel subscription
    const _cancelActiveSubscription = async (sub_id, product_name) => {

        let url = "subscription/cancel";
        let data = {
            "id": Number(sub_id)
        };

        const response = await APIService(url, data);
        if (response.status_code === 200) {
            let success_msg = `${product_name} subscription cancelled successfully`;
            setDataLoading(true);
            let cancelled_list = [sub_id, ...cancelledIDs];
            setCancelledIDs(cancelled_list);
            _getSubscriptionList(success_msg, cancelled_list);
        } else {
            toast.dismiss();
            toast.error(response.message);
            setBtnLoading(false);
        }

    };

    return (
        <section className="min-vh-100">
            <span className={`e-font-16-res e-text-primary-color e-primary-font-clash-display e-font-weight-500 e-font-20 line-height-24px`}>
                Subscriptions
            </span>

            <div className={`${initialRender.current === false ? "col-lg-8" : "col-lg-12"}  mt-4`}>
                <div className="card-box-shadow e-bg-white border-radius-16 p-3 min-vh-100 mb-5 pb-5">
                    {
                        dataLoading ?
                            <Loader wrapperClassName="h-100 margin-100px-top" />
                            :
                            <>

                                {
                                    initialRender.current === false &&
                                    <div className='row mb-2'>
                                        <div className='d-flex align-items-center gap-2 justify-content-end position-relative'>
                                            <div className=' e-padding-8px-tb-12px-lr-res d-flex gap-6px align-items-center e-border-0-5-ocean-blue e-bg-magnolia px-3 padding-10px-tb border-radius-100 cursor-pointer'
                                                onClick={_handleToggleFilter}>
                                                <div className='d-flex position-relative'>
                                                    <img src={require("../../Assets/Images/Other/filter.svg").default}
                                                        width={18} />
                                                    {/* {
                                                        (selectedStatusIds.length + selectedProductTypeIds.length) !== 0 &&
                                                        <span class="position-absolute top-0 left-84-per translate-middle badge rounded-pill e-bg-filter-count e-text-white e-alt-font-lato e-font-8 e-font-weight-700 line-height-10px px-1 padding-1px-top padding-2px-bottom">
                                                            {selectedStatusIds.length + selectedProductTypeIds.length}
                                                        </span>
                                                    } */}
                                                </div>
                                                <p className='e-font-12-res mb-0 e-text-chinese-black e-alt-font-lato e-font-14 e-font-weight-600 line-height-20px'>
                                                    Filter
                                                </p>
                                            </div>
                                            {/* Filter modal */}
                                            {
                                                toggleFilter &&
                                                <>
                                                    <OverLay isOpen={toggleFilter}
                                                        onClose={_handleCancelFilter} />
                                                    <SubscriptionFilterModal
                                                        activeStatus={ACTIVE_STATUS}
                                                        // productType={PRODUCT_TYPE}
                                                        selectedStatus={selectedStatusIds}
                                                        selectedProductType={selectedProductTypeIds}
                                                        handleSelectStatus={_handleFilterByStatus}
                                                        handleSelectProductType={_handleFilterByProduct}
                                                        handleClear={_handleClearFilter}
                                                        handleCancel={_handleCancelFilter}
                                                        handleApply={_handleApplyFilter} />
                                                </>
                                            }
                                        </div>
                                    </div>
                                }

                                {
                                    // Subscription Card 
                                    activeList.length !== 0 ?
                                        <div div className="row">
                                            <div className="col-sm-10">
                                                <div className="d-flex flex-column gap-3">
                                                    {
                                                        activeList.map((product, index) => (
                                                            <SubscriptionCard key={index}
                                                                product={product}
                                                                loading={btnLoading && subsID === product.id ? true : false}
                                                                handleRenew={() => { }}
                                                                handleViewBreakDown={() => _handleOpenPaymentBreakDownModal(product)}
                                                                handleCancelSubscription={() => _handleCancelSubscription(product.id, product.strategy_details.strategy_name)} />
                                                        ))
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                        :
                                        <EmptyScreen
                                            type={initialRender.current === false ? 2 : 1}
                                            emptyScreenImage={EmptySubscription}
                                            description={initialRender.current === false ? "No subscriptions found for the selected filters, try different filters" : "You don't have any active subscription. Subscribe now and start growing your wealth."}
                                            btnLabel={initialRender.current === false ? "" : "Subscribe Now"}
                                            onClick={_handleStartInvesting} />
                                }
                            </>
                    }
                </div>
            </div>

            {/* Subscription payment breakdown modal */}
            {
                showPaymentBreakDownModal &&
                <PaymentBreakDownModal breakDownInfo={breakDownInfo.current}
                    handleClose={_handleClosePaymentBreakdownModal} />
            }

        </section >
    )
}

export default SubscriptionListing;